/**
 * Navigation config for the custom Topbar.
 *
 * Drives the static "Utforsk" (Explore) link list and the
 * authenticated user dropdown menu items. Edit here when adding/removing
 * utility links; pages that don't yet exist fall back to LandingPage so
 * nothing dead-ends.
 *
 * Category navigation is handled by `src/components/CategoryMenu/` — it
 * reads `categoryConfiguration` directly via `useConfiguration()` rather
 * than going through this module.
 */

// Pathname prefixes for pages that render the account-settings UserNav
// (ContactDetailsPage, PasswordChangePage, PaymentMethodsPage,
// NotificationSettingsPage / ManageAccountPage, ProfileSettingsPage,
// ManageListingsPage, FavoriteListingsPage, InboxPage, StripePayoutPage,
// TransactionPage's /order and /sale routes).
// CategoryMenu and MobileBottomNav suppress themselves on these routes
// — the account surfaces have their own navigation chrome and adding
// the category strip / bottom nav would clutter the UI.
const USER_NAV_PATHNAME_PREFIXES = [
  '/account',
  '/profile-settings',
  '/listings',
  '/favorites',
  '/inbox',
  '/order',
  '/sale',
];

export const isUserNavPathname = pathname => {
  if (!pathname) {
    return false;
  }
  return USER_NAV_PATHNAME_PREFIXES.some(prefix => {
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
};

// Account/task surfaces where the mobile bottom nav is suppressed —
// they have their own navigation chrome. Deliberately a SUBSET of the
// user-nav pages: Messages (`/inbox`) is intentionally excluded so the
// bottom nav stays visible there (client request), even though it's
// still a user-nav page for CategoryMenu/side-nav purposes via
// isUserNavPathname. `/order` and `/sale` (transaction detail) keep the
// nav hidden so buyers/sellers stay focused on the active order.
const MOBILE_BOTTOM_NAV_HIDDEN_PREFIXES = [
  '/account',
  '/profile-settings',
  '/listings',
  '/favorites',
  '/order',
  '/sale',
];

// The edit-listing wizard lives under `/l/...`: `/l/new` (create) and
// `/l/:slug/:id/:type/:tab[/:returnURLType]` (edit/draft). Those are the
// only `/l` routes with 5+ path segments (or exactly `/l/new`). Listing
// detail (`/l/:slug/:id`), checkout, make-offer, request-quote and
// variants have fewer segments.
export const isEditListingWizardPathname = pathname => {
  if (!pathname) {
    return false;
  }
  if (pathname === '/l/new') {
    return true;
  }
  const segments = pathname.split('/').filter(Boolean);
  return segments[0] === 'l' && segments.length >= 5;
};

// Where the mobile bottom nav is suppressed. The account/task surfaces
// above, plus the listing family (`/l/...`) EXCEPT the edit-listing
// wizard, where the client wants the bottom nav to persist through every
// step (photo → AI analysis → review → shipping → confirm → publish).
// Checkout (`/l/:slug/:id/checkout`) stays hidden so buyers don't
// wander off mid-purchase.
export const isMobileBottomNavHidden = pathname => {
  if (!pathname) {
    return false;
  }
  const matchesHiddenPrefix = MOBILE_BOTTOM_NAV_HIDDEN_PREFIXES.some(prefix => {
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
  if (matchesHiddenPrefix) {
    return true;
  }
  if (pathname === '/l' || pathname.startsWith('/l/')) {
    return !isEditListingWizardPathname(pathname);
  }
  return false;
};

/**
 * Authenticated user menu items for the desktop avatar dropdown.
 *
 * Items resolve to NamedLinks. Pages that don't exist in the template are
 * marked `placeholder: true` and fall back to LandingPage.
 *
 * The logout item has `isLogout: true` and is rendered as a button (logout
 * is a Redux action, not a navigation).
 */
// Mirrors the desktop UserNav order so the "Mine sider" section in
// TopbarMobileMenu matches what a signed-in user sees on the account
// pages: Listings → Inbox → Favorites → Profile → Account. Inbox
// duplicates the MobileBottomNav messages affordance but the client
// wants both surfaces to carry the link.
export const userMenuItems = [
  {
    key: 'listings',
    labelMessageId: 'Topbar.userMenuYourListings',
    name: 'ManageListingsPage',
  },
  {
    key: 'inbox',
    labelMessageId: 'Topbar.userMenuInbox',
    name: 'InboxPage',
    params: { tab: 'sales' },
  },
  {
    key: 'favorites',
    labelMessageId: 'Topbar.userMenuFavorites',
    name: 'FavoriteListingsPage',
  },
  {
    key: 'profileSettings',
    labelMessageId: 'Topbar.userMenuProfileSettings',
    name: 'ProfileSettingsPage',
  },
  {
    key: 'accountSettings',
    labelMessageId: 'Topbar.userMenuAccountSettings',
    name: 'ContactDetailsPage',
  },
];

export const userMenuLogout = {
  key: 'logout',
  labelMessageId: 'Topbar.userMenuLogout',
  isLogout: true,
};
